import { useCallback, useEffect, useState } from 'react'

import { MeInformationService, OpenAPI } from '@api'
import { useUnit } from 'effector-react'
import { useMutation } from 'react-query'

import { useToast } from '@/components/ui/toast'
import { LocalStorageKeys } from '@/consts/localStorage.ts'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { $account } from '@/models/account.ts'
import { openDialog } from '@/models/dialogs'
import { handleError } from '@/utils/error'

import { useAccount } from '../../hooks/useAccounts'
import { authService } from '../../service/auth-service'
import type { AccountLoginRequest } from '../../types'
import type { SavedAccount } from './types'

export const useMultipleAccounts = () => {
  const account = useUnit($account)
  const { loginWithToken, accessToken } = useAccount()
  const toast = useToast()

  const [loadingUserId, setLoadingUserId] = useState<number | null>(null)
  const [accounts, setAccounts] = useState<SavedAccount[]>(() => {
    const accs = localStorage.getItem(LocalStorageKeys.ACCOUNTS)

    return accs ? JSON.parse(accs) : []
  })

  useEffect(
    () => {
      if (accounts.length || !account || !accessToken) {
        return
      }
      setAccounts([
        {
          id: account.userId!,
          token: accessToken,
          name: account.name!,
          email: account.email!,
          profilePicture: account.profilePicture!,
        },
      ])
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account],
  )

  const handleAddUserLogin = useCallback(
    async (body: { email: string; token: string }) => {
      toast.success('Account added successfully. Switching account...')
      // TODO: check it, probably need to define header
      OpenAPI.TOKEN = body.token

      const profile = await MeInformationService.findMeInformation()
      const newUser: SavedAccount = {
        id: profile.userId!,
        token: body.token,
        name: profile.name!,
        email: profile.email!,
        profilePicture: profile.profilePicture!,
      }

      const existingUser = accounts.find((user) => user.id === profile.userId)
      const updatedAccounts = existingUser
        ? accounts.map((user) => (user.id === existingUser.id ? newUser : user))
        : [...accounts, newUser]

      setAccounts(updatedAccounts)
      localStorage.setItem(
        LocalStorageKeys.ACCOUNTS,
        JSON.stringify(updatedAccounts),
      )

      loginWithToken(newUser.token)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts],
  )

  const loginMutation = useMutation((loginData: AccountLoginRequest) =>
    authService.login(loginData),
  )

  const openLoginDialog = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.ADD_ACCOUNT,
      onAddUser: handleAddUserLogin,
      loginMutation,
    })
  }, [handleAddUserLogin, loginMutation])

  const handleUserLogout = useCallback(
    (userId: number) => {
      const updatedAccounts = accounts.filter((user) => user.id !== userId)

      setAccounts(updatedAccounts)
      localStorage.setItem(
        LocalStorageKeys.ACCOUNTS,
        JSON.stringify(updatedAccounts),
      )
    },
    [accounts],
  )

  const handleUserLogin = useCallback(
    async (user: SavedAccount) => {
      try {
        setLoadingUserId(user.id)
        // Check if the token is still valid
        // TODO: check it, probably need to define header
        OpenAPI.TOKEN = user.token
        await MeInformationService.findMeInformation()
        toast.success('Switching account')
        loginWithToken(user.token)
      } catch (error) {
        handleError(error, 'Session expired, please login again')
        openLoginDialog()
      } finally {
        setLoadingUserId(null)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return {
    accounts,
    loginMutation,
    loadingUserId,
    openLoginDialog,
    onUserLogout: handleUserLogout,
    onUserLogin: handleUserLogin,
  }
}
